import { AuthenticatedHttp, generateCSRF, GuestHttp } from '@/http/axios';
import { AppError } from '@/utils/api';

export const RECAPTCHA_ERROR_MESSAGE =
  'Detectamos um problema com sua solicitação. Por favor, tente novamente usando um dispositivo ou navegador diferente.';

export const RECAPTCHA_ERROR_CODE = 'Recaptcha validation failed.';

const getAccessTokenFromAuthResponse = (data = {}) =>
  data.access || data.access_token || data.token || data.jwt || null;

const getRefreshTokenFromAuthResponse = (data = {}) =>
  data.refresh || data.refresh_token || null;

export const login = ({ email, password, recaptchaToken }) =>
  GuestHttp.post('/token/', {
    email,
    password,
    recaptchaToken,
  })
    .then(({ data }) => {
      if (data.ephemeral_token) {
        return {
          type: '2fa',
          ephemeralToken: data.ephemeral_token,
          methods: data.methods,
        };
      }

      return {
        type: 'direct',
        accessToken: getAccessTokenFromAuthResponse(data),
        refreshToken: getRefreshTokenFromAuthResponse(data),
      };
    })
    .catch((error) => {
      if (
        error?.response?.status === 400 &&
        error?.response?.data?.detail === RECAPTCHA_ERROR_CODE
      ) {
        throw new AppError({
          message: RECAPTCHA_ERROR_MESSAGE,
        });
      }
      throw new AppError(error);
    });

export const logout = async () => {
  await AuthenticatedHttp.post('/session-logout/')
}

export const loginSecondStepService = (ephemeral_token, code) =>
  GuestHttp.post('/token/code/', {
    ephemeral_token,
    code,
  })
    .then(({ data }) => ({
      accessToken: getAccessTokenFromAuthResponse(data),
      refreshToken: getRefreshTokenFromAuthResponse(data),
    }))
    .catch((error) => {
      if (error?.response?.data?.detail === RECAPTCHA_ERROR_CODE) {
        throw new AppError({
          message: RECAPTCHA_ERROR_MESSAGE,
        });
      }
      throw new AppError(error);
    });

export const recoverySendCodeService = ({ email, recaptchaToken }) =>
  GuestHttp.post('/recovery/send-email/', {
    email,
    recaptchaToken,
  }).catch((error) => {
    if (error?.response?.data?.detail === RECAPTCHA_ERROR_CODE) {
      throw new AppError({
        message: RECAPTCHA_ERROR_MESSAGE,
      });
    }
    throw new AppError(error);
  });

export const recoveryChangePasswordService = ({ token, password }) =>
  GuestHttp.post('/recovery/change-password/', {
    token,
    password,
  }).catch((error) => {
    throw new AppError(error);
  });

// O cadastro e a validação do código abrem sessão Django, então levam o cookie e o CSRF.
const sessionRequestConfig = async () => {
  const csrfToken = await generateCSRF({ forceRefresh: true });

  return {
    withCredentials: true,
    headers: {
      ...(csrfToken && { 'X-CSRFToken': csrfToken }),
    },
  };
};

const toSessionTokens = ({ user_id, ...tokens }) => ({
  accessToken: getAccessTokenFromAuthResponse(tokens),
  refreshToken: getRefreshTokenFromAuthResponse(tokens),
  userId: user_id,
});

// `ref` é o código do Cakto Partners. Vai na query porque o backend só lê de lá
// e sobrescreve qualquer `referral_code` mandado no corpo.
export const register = async (values, { ref } = {}) => {
  try {
    const { data } = await GuestHttp.post('/register/', values, {
      ...(await sessionRequestConfig()),
      ...(ref && { params: { ref } }),
    });

    if (data.attempt_id) {
      return { kind: 'attempt', attemptId: data.attempt_id, detail: data.detail };
    }

    return { kind: 'tokens', ...toSessionTokens(data) };
  } catch (error) {
    throw new AppError(error);
  }
};

export const validateSignup = async ({ attemptId, code }) => {
  try {
    const { data } = await GuestHttp.post(
      '/register/validate/',
      { attempt_id: attemptId, code },
      await sessionRequestConfig()
    );

    return toSessionTokens(data);
  } catch (error) {
    throw new AppError(error);
  }
};

export const resendSignupCode = ({ attemptId }) =>
  GuestHttp.post('/register/resend/', { attempt_id: attemptId })
    .then(({ data }) => data)
    .catch((error) => {
      throw new AppError(error);
    });

// O cadastro pendente do Google vive num cookie HttpOnly que o SSO grava para o domínio da API;
// sem as credenciais o backend não enxerga o pendente e responde sempre `pending: null`.
export const getGoogleSignup = async () => {
  try {
    const { data } = await GuestHttp.get('/register/google/', { withCredentials: true });

    return { enabled: Boolean(data?.enabled), pending: data?.pending ?? null };
  } catch (error) {
    throw new AppError(error);
  }
};

// Sem e-mail e sem senha no corpo: o e-mail sai do pendente no servidor. O `ref` vai na query
// porque é de lá que o backend lê a indicação (`normalize_signup_ref`), como no `/register/`.
export const registerWithGoogle = async (values, { ref } = {}) => {
  try {
    const { data } = await GuestHttp.post('/register/google/', values, {
      ...(await sessionRequestConfig()),
      ...(ref && { params: { ref } }),
    });

    return { ...toSessionTokens(data), next: data.next ?? null };
  } catch (error) {
    throw new AppError(error);
  }
};

export const me = ({ forceUpdate = false } = {}, config = {}) =>
  AuthenticatedHttp.get('/user/', {
    ...config,
    params: {
      ...(forceUpdate && { force_update: 1 }),
    },
  }).then(({ data: user }) => ({
    ...user,
    lastUpdate: user.last_update || user.lastUpdate,
    picture:
      user.picture || 'https://api-dev-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg',
  }));

let meCache = null;
let meInflight = null;

export const clearMeCache = () => {
  meCache = null;
  meInflight = null;
};

export const patchMeCache = (patch) => {
  if (!meCache) return;
  meCache = { ...meCache, ...patch };
};

export const meCached = async ({ forceUpdate = false } = {}) => {
  if (forceUpdate) {
    meCache = null;
    meInflight = null;
  }

  if (meCache) return meCache;
  if (meInflight) return meInflight;

  meInflight = me({ forceUpdate })
    .then((user) => {
      meCache = user;
      meInflight = null;
      return user;
    })
    .catch((e) => {
      meInflight = null;
      throw e;
    });

  return meInflight;
};

export const getOtpToken = (token) =>
  token ? GuestHttp.post(`/login/otp/`, { token }).then(({ data }) => data) : { redirect: true };
